import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import PageHeader from "components/PageLayout";
import Skeleton from "components/Skeleton";
import EmptyState from "components/EmptyState";
import { requireAuth } from "utilities/auth";
import { useRequestTypesAdmin } from "hooks/useRequestTypesAdmin";
import { TICKET_PRIORITY_LABELS } from "constants/tickets";
import { sortRequestTypes } from "utilities/requestTypes";
import { Tags, Plus, Search, Star } from "lucide-react";

/**
 * RequestTypes (SOUND-544) — the request-type vocabulary as data. Lists every
 * type, inactive included (dimmed + badged, never hidden: a deactivated type
 * still labels historical tickets). list → editor route, mirrors SLA Policies.
 * Internal-only. Reads `/ticket-request-types`, never a hardcoded list.
 */
function RequestTypes() {
  const navigate = useNavigate();
  const { requestTypes, loading, error } = useRequestTypesAdmin({ includeInactive: true });
  const [search, setSearch] = useState("");
  const [showInactive, setShowInactive] = useState(true);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return sortRequestTypes(requestTypes).filter(
      (rt) =>
        (showInactive || rt.active !== false) &&
        (!q || (rt.label || "").toLowerCase().includes(q) || (rt.key || "").includes(q))
    );
  }, [requestTypes, search, showInactive]);

  const inactiveCount = requestTypes.filter((rt) => rt.active === false).length;

  return (
    <div>
      <PageHeader
        title="Request Types"
        subtitle="The ticket vocabulary. A key is permanent — it joins workflows, SLA policies, Telegram commands and every historical ticket — so a rename is a label edit. Types are deactivated, never deleted."
        actions={
          <button className="btn btn-primary" onClick={() => navigate("/settings/request-types/new")}>
            <Plus className="h-4 w-4" />
            New request type
          </button>
        }
      />

      <div className="container space-y-4 py-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="relative max-w-sm flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              className="input pl-9"
              placeholder="Search label or key…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-muted-foreground">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-border"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
            />
            Show inactive{inactiveCount > 0 ? ` (${inactiveCount})` : ""}
          </label>
        </div>

        {error && (
          <div className="rounded-lg border border-destructive/30 bg-destructive/[0.06] p-4 text-sm text-foreground">
            Failed to load request types: {error.response?.data?.error || error.message || "Unknown error"}
          </div>
        )}

        {loading ? (
          <div className="space-y-3">
            <Skeleton.Rect className="h-12 w-full" />
            <Skeleton.Rect className="h-12 w-full" />
            <Skeleton.Rect className="h-12 w-2/3" />
          </div>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={Tags}
            heading={search ? "No matching request types" : "No request types yet"}
            description={search ? "Try a different search." : "Create the first request type."}
          />
        ) : (
          <div className="surface overflow-hidden rounded-xl ring-1 ring-border">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    {["Request type", "Key", "Audience", "Default priority", "Focus", "Status"].map((c) => (
                      <th
                        key={c}
                        className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground"
                      >
                        {c}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {rows.map((rt) => {
                    const inactive = rt.active === false;
                    return (
                      <tr
                        key={rt.key}
                        className={`cursor-pointer transition-colors hover:bg-muted ${inactive ? "opacity-60" : ""}`}
                        onClick={() => navigate(`/settings/request-types/${encodeURIComponent(rt.key)}`)}
                      >
                        <td className="px-4 py-3">
                          <div className={`font-medium text-foreground ${inactive ? "line-through decoration-muted-foreground/60" : ""}`}>
                            {rt.label}
                          </div>
                          {rt.description && (
                            <div className="mt-0.5 line-clamp-1 text-xs text-muted-foreground">{rt.description}</div>
                          )}
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{rt.key}</td>
                        <td className="px-4 py-3">
                          <span className="badge badge-muted">
                            {(rt.audience || []).includes("client") ? "Internal + Client" : "Internal only"}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">
                          {rt.defaultPriority ? TICKET_PRIORITY_LABELS[rt.defaultPriority] || rt.defaultPriority : "—"}
                        </td>
                        <td className="px-4 py-3">
                          {rt.focus ? (
                            <span className="inline-flex items-center gap-1 text-foreground">
                              <Star className="h-3.5 w-3.5 text-brand" />
                              On board
                            </span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`badge ${inactive ? "badge-muted" : "badge-success"}`}>
                            {inactive ? "Inactive" : "Active"}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default requireAuth(RequestTypes);
